import { HttpError } from '@/api/apiClient';

/**
 * Human-readable message from apiClient/fetch failures (FastAPI `detail` string or validation array,
 * or the backend AppError shape `{message}` — already localized server-side).
 *
 * By default the FastAPI validation shapes (array / object `detail`) are turned
 * into a readable string too. Pass `stringDetailOnly` when the caller only wants
 * to surface controlled backend messages (a plain-string `detail`, e.g. our own
 * `*ValidationError.user_message`) and treat every framework-generated 422 as the
 * generic fallback — so raw Pydantic details ("Field required", field locations)
 * never reach the user.
 *
 * Pass `genericOnServerError` to collapse every 5xx to the fallback. Those bodies
 * carry infrastructure detail ("External API error") that is neither actionable nor
 * localized, unlike the 4xx `detail` strings we author ourselves.
 */
export function getApiErrorMessage(
	error: unknown,
	fallback: string,
	options: { stringDetailOnly?: boolean; genericOnServerError?: boolean } = {}
): string {
	const { stringDetailOnly = false, genericOnServerError = false } = options;
	if (error instanceof HttpError) {
		if (genericOnServerError && error.status >= 500) {
			return fallback;
		}
		const match = error.message.match(/^HTTP \d+:\s*(.*)$/s);
		const payload = match?.[1]?.trim();
		if (payload) {
			try {
				const parsed = JSON.parse(payload) as {
					detail?:
					| string
					| Array<{ msg?: string; message?: string } | string>
					| Record<string, unknown>;
					message?: string;
				};
				// String
				if (typeof parsed?.detail === 'string') {
					return parsed.detail;
				}
				// AppError shape: a top-level, already-localized `message` (no `detail`).
				if (typeof parsed?.message === 'string' && parsed.message.trim()) {
					return parsed.message;
				}
				if (stringDetailOnly) {
					return fallback;
				}
				// Array
				if (Array.isArray(parsed?.detail)) {
					const parts = parsed.detail.map((d) =>
						typeof d === 'string' ? d : d?.msg || d?.message || JSON.stringify(d)
					);
					return parts.filter(Boolean).join('; ') || fallback;
				}
				// Object
				if (parsed?.detail && typeof parsed.detail === 'object') {
					const detail = parsed.detail as Record<string, unknown>;
					const candidates = [
						detail.user_message,
						detail.message,
						detail.msg,
						detail.error,
					];
					const message = candidates.find(
						(value): value is string =>
							typeof value === 'string' && value.trim().length > 0
					);
					if (message) {
						return message;
					}

					const missingFields = detail.missing_fields;
					if (Array.isArray(missingFields) && missingFields.length > 0) {
						return `Campos obrigatórios pendentes: ${missingFields.join(', ')}.`;
					}
				}
				// Default
				if (parsed?.detail != null) {
					return String(parsed.detail);
				}
			} catch {
				return payload;
			}
		}
		return error.message || fallback;
	}
	if (error instanceof Error && error.name === 'AbortError') {
		return fallback;
	}
	if (error instanceof Error && error.message) {
		return error.message;
	}
	return fallback;
}

export function isAbortError(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	if (error.name === 'AbortError') return true;
	// Defensive: some environments surface aborts as a generic Error mentioning "abort".
	return /abort/i.test(error.message);
}

/** Stable code from a structured FastAPI error response. */
export function getApiErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpError)) return null;
    const body = error.body;
    if (!body || typeof body !== 'object' || !('detail' in body)) {
        return null;
    }
    const detail = body.detail;
    if (!detail || typeof detail !== 'object' || !('code' in detail)) {
        return null;
    }
    return typeof detail.code === 'string' ? detail.code : null;
}

/** True when the backend rejected the request for lack of tokens (HTTP 402 Payment Required). */
export function isPaymentRequiredError(error: unknown): boolean {
	return error instanceof HttpError && error.status === 402;
}
